import {
  PoliciesSource,
  PolicyBuilderGraph,
  PolicyBuilderGraphVertices,
  VertexMap,
} from '../types';
import { pathForSecurityVertex } from '../util/pathForSecurityVertex';

const initialState: PolicyBuilderGraph = {
  edges: {
    byRelationshipKey: {},
    known: [],
  },
  vertices: {
    byPolicyId: {},
    byProcedureId: {},
    byReferenceId: {},
    byTemplatePath: {},
    known: [],
  },
};

export default initialState;

// Since these reducers aren't partially applied as part of integration
// with React's useState hook, they have a more standard function signature

export const getVertexMap = (
  state: PolicyBuilderGraph,
  source: PoliciesSource
): VertexMap => {
  const { byPolicyId, byProcedureId, byReferenceId } = state.vertices;
  switch (source) {
    case PoliciesSource.Policies:
      return byPolicyId;
    case PoliciesSource.Procedures:
      return byProcedureId;
    case PoliciesSource.References:
      return byReferenceId;
    default:
      throw new Error(
        `Unable to retrieve vertex map for invalid source ${source}`
      );
  }
};

export const addEdge = (state: PolicyBuilderGraph, edge: any) => ({
  ...state,
  edges: {
    ...state.edges,
    known: [...state.edges.known, edge],
    byRelationshipKey: {
      ...state.edges.byRelationshipKey,
      [edge.relationship._key]: edge,
    },
  },
});

export const addVertexIfNeeded = (state: PolicyBuilderGraph, vertex: any) => {
  const id = vertex.properties.id || vertex.properties.name;

  let categoryKey: keyof Omit<PolicyBuilderGraphVertices, 'known'> | undefined;
  const byTemplatePathKey = pathForSecurityVertex(vertex);

  switch (vertex.entity._type[0]) {
    case 'security_policy': {
      categoryKey = 'byPolicyId';
      break;
    }
    case 'security_procedure': {
      categoryKey = 'byProcedureId';
      break;
    }
    case 'security_document': {
      categoryKey = 'byReferenceId';
      break;
    }
  }

  if (!categoryKey || !byTemplatePathKey || state.vertices[categoryKey][id]) {
    // We only add the vertex "if needed". While the first two conditions above
    // should never be triggered, the last one helps us avoid a duplicate insertion.
    return state;
  }

  return {
    ...state,
    vertices: {
      ...state.vertices,
      known: [...state.vertices.known, vertex],
      [categoryKey]: {
        ...state.vertices[categoryKey],
        [id]: vertex,
      },
      byTemplatePath: {
        ...state.vertices.byTemplatePath,
        [byTemplatePathKey]: vertex,
      },
    },
  };
};
